import { useState } from 'react';
import {
  mapCompanySiteType,
  mapCompanyType,
  useFindCompanySitesSuspense,
  useFindCompanySuspense,
  type CompanySiteType,
} from '@api';
import {
  AddActionIcon,
  AddCompanySiteFormSchemaType,
  AddCompanySiteModal,
  TouringCard,
  TouringCardActions,
  TouringCardHeader,
  TouringCardSection,
  TouringCardTitle,
  useCreateCompanySite,
} from '@common';
import { usePermissionContext } from '@hypeit-gmbh/hypeit-ui-core';
import { Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { Route } from 'src/routes/_rootLayout/companies/$id.general';
import { CompanySitesTable, UpdateCompanySiteForm } from './components';
import { useDeleteCompanySite, useSetCompanySiteAsHeadquarter } from './hooks';

export const CompanySites: React.FC = () => {
  const { t } = useTranslation('companyDetailsGeneral');
  const { id } = Route.useParams();
  const { data: companyName } = useFindCompanySuspense(id, {
    query: { select: (data) => mapCompanyType(data.data).name },
  });
  const { data: records } = useFindCompanySitesSuspense(id, {
    query: { select: (data) => data.data.map(mapCompanySiteType) },
  });
  const { handleDeleteCompanySite } = useDeleteCompanySite(id);
  const { setSiteAsHeadquarter } = useSetCompanySiteAsHeadquarter(id);
  const { createCompanySite } = useCreateCompanySite(id);
  const [editSite, setEditSite] = useState<CompanySiteType | null>(null);
  const [addSiteOpened, { open: openAddSite, close: closeAddSite }] =
    useDisclosure(false);
  const { hasPermission } = usePermissionContext();
  const userCanWriteSites = hasPermission({
    permission: 'ADDRESSBOOK_COMPANIES',
    action: 'WRITE',
  });

  const handleEditCompanySite = (site: CompanySiteType): void => {
    setEditSite(site);
  };

  const handleCloseEdit = (): void => {
    setEditSite(null);
  };

  const handleCreateCompanySite = async (
    formValues: AddCompanySiteFormSchemaType
  ): Promise<void> => {
    await createCompanySite({
      address: {
        ...formValues.address,
        cityEnglish: null,
        state: null,
      },
      contact: {
        ...formValues.contact,
        mobile: null,
      },
    });
  };

  return (
    <>
      <TouringCard data-testid="company-sites-card">
        <TouringCardHeader>
          <TouringCardTitle>{t('COMPANY_SITES.TITLE')}</TouringCardTitle>
          <TouringCardActions>
            {userCanWriteSites && (
              <AddActionIcon
                onClick={openAddSite}
                aria-label={t('COMPANY_SITES.ADD_SITE_BUTTON')}
              />
            )}
          </TouringCardActions>
        </TouringCardHeader>
        <TouringCardSection>
          <CompanySitesTable
            records={records}
            handleDeleteCompanySite={handleDeleteCompanySite}
            handleEditCompanySite={handleEditCompanySite}
            handleSetSiteAsHeadquarter={setSiteAsHeadquarter}
          />
        </TouringCardSection>
      </TouringCard>
      <AddCompanySiteModal
        opened={addSiteOpened}
        onClose={closeAddSite}
        onSubmit={handleCreateCompanySite}
        companyName={companyName}
      />
      <Modal
        opened={editSite !== null}
        onClose={handleCloseEdit}
        size="xl"
        title={t('COMPANY_SITES.UPDATE_SITE_MODAL.TITLE')}
      >
        {editSite ? (
          <UpdateCompanySiteForm
            key={editSite.id}
            companyId={id}
            site={editSite}
            onClose={handleCloseEdit}
          />
        ) : null}
      </Modal>
    </>
  );
};
